import { Grid, Box, Paper, Button, Typography } from "@mui/material";
import { ServiceCase, useDownloadData, useUpdateServiceCase, useGetServiceCase } from "@coherehealth/qm-api";
import { blobFromRequest, downloadUrl, queueMgmtBaseUrl } from "@coherehealth/common";
import { useTheme } from "@material-ui/core/styles";
import { useSearchParams } from "react-router-dom";
import PageHeader from "../common/PageHeader";

export default function WiserDownloadPage() {
  const theme = useTheme();

  const CaseActions = ({ serviceCase }: { serviceCase?: ServiceCase }) => {
    const [searchParams] = useSearchParams();
    const caseIdFromUrl = searchParams.get("caseId");
    const caseId = serviceCase?.id || caseIdFromUrl || "";

    const { data: fetchedCase } = useGetServiceCase({ id: caseId || "", lazy: !caseId });
    const currentCase = serviceCase || fetchedCase;

    const { refetch: downloadData } = useDownloadData({ id: caseId || "", lazy: true });
    const { mutate: updateServiceCase } = useUpdateServiceCase({ id: caseId });

    const handleDownload = async () => {
      try {
        const blob = await blobFromRequest(downloadData({}) as Promise<null | void>);
        if (blob) {
          downloadUrl(URL.createObjectURL(blob), `case-data-${caseId}.zip`);
        }
      } catch (error) {
        console.error("Download error:", error);
      }
    };

    const handleComplete = async () => {
      try {
        await updateServiceCase({ caseStatus: "COMPLETE" });
        window.location.assign(`${queueMgmtBaseUrl()}/case_complete/${caseId}`);
      } catch (error) {
        console.error("Error updating case status:", error);
      }
    };

    // TODO do we probably need some way to mark case status as CANCELLED
    return (
      <Grid item sx={{ display: "flex", width: "100%", justifyContent: "center" }}>
        <Paper elevation={1} sx={{ width: "900px", mt: 4, p: 3 }}>
          <Box display="flex" flexDirection="column" gap={3}>
            <Box>
              <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 2 }}>
                Case Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                    Case ID: {caseId || "Not specified"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                    Case Number: {currentCase?.caseNumber || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                    Status: {currentCase?.caseStatus || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                    Type: {currentCase?.caseType || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                    Assignee: {currentCase?.assignee?.name || "Unassigned"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                    Received Date:{" "}
                    {currentCase?.fileDetail?.fileReceivedTime
                      ? new Date(currentCase.fileDetail.fileReceivedTime).toLocaleString()
                      : "N/A"}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                    Priority: {currentCase?.casePriority || "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Box
              display="flex"
              justifyContent="space-around"
              sx={{ pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}
            >
              <Button variant="contained" color="primary" onClick={handleDownload} sx={{ minWidth: 200 }}>
                Download Case Data
              </Button>
              <Button
                variant="contained"
                onClick={handleComplete}
                sx={{
                  minWidth: 200,
                  backgroundColor: theme.palette.success.main,
                  "&:hover": { backgroundColor: theme.palette.success.dark },
                }}
              >
                Mark Complete
              </Button>
            </Box>
          </Box>
        </Paper>
      </Grid>
    );
  };

  return (
    <Grid container sx={{ padding: theme.spacing(4), flexDirection: "column", overflow: "hidden" }}>
      <Grid item>
        <PageHeader headerLabel="Case Management" />
      </Grid>
      <CaseActions />
    </Grid>
  );
}
